import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {ApiClient, ApiError, newIdempotencyKey} from '../api';
import type {GuestSession, OpenGuestSessionRequest, ServiceMode} from '../api/types';
import {readConfig} from '../app/config';
import {StorageKeys, clearGuestData, readJson, remove, writeJson} from '../app/storage';
import {BCP47, type Language} from '../i18n';

/**
 * The guest session — the one piece of state everything else hangs off.
 *
 * ## No install, no account, no login
 *
 * The credential is the token printed on the table. There is no registration
 * step anywhere in this file and there must not be one: a guest asked to
 * register leaves. PDPA follows from that rather than being bolted on — no
 * name, no phone, no email is collected to order, so there is nothing here to
 * consent to.
 *
 * ## Why the session is persisted
 *
 * A guest who switches to another app and comes back, or whose browser reloads
 * the tab under memory pressure — both routine on a cheap phone — must not have
 * to find the QR again. The session is written to storage and re-read on start,
 * and the server is asked to confirm it rather than the client trusting its own
 * copy of `expires_at`.
 *
 * ## Why the API client lives here
 *
 * The client needs the token; the token lives in this state. Putting the client
 * anywhere else means a second source of truth for "who am I right now", which
 * is the bug that ends with a request going out under a stale credential after
 * a table QR was regenerated.
 */

export type SessionStatus =
  | 'idle' // nothing scanned yet — the guest typed the URL or bookmarked it
  | 'opening' // POST /v1/guest/sessions in flight
  | 'active'
  | 'expired' // the server rejected a credential this client thought was good
  | 'invalid' // the QR itself is not a table QR for this tenant
  | 'error'; // network, or something this client could not read

export interface OpenSessionInput {
  tableQrToken: string;
  /** Printed beside the token. Required in practice (D-019) — a scan without it is refused. */
  tenantId?: string;
  tableId?: string;
  /** `dine_in` from a table QR (Q1), `queue_ahead` from a counter QR (Q12). */
  serviceMode: ServiceMode;
  preferredLanguage?: Language;
}

export interface GuestSessionValue {
  status: SessionStatus;
  session: GuestSession | null;
  error: ApiError | null;
  /** The API client, bound to whatever credential is current. */
  client: ApiClient;
  /** Open a session from a scanned QR. Safe to call twice — it is idempotent. */
  open: (input: OpenSessionInput) => Promise<void>;
  /** Re-read the session from the server. Used on resume and after a long idle. */
  refresh: () => Promise<void>;
  /** Forget everything. The guest is back to needing a scan. */
  clear: () => void;
}

const GuestSessionContext = createContext<GuestSessionValue | null>(null);

interface StoredSession {
  session: GuestSession;
  /** Which QR opened it, so a re-scan of the same table is recognised as a resume. */
  tableQrToken: string;
  serviceMode: ServiceMode;
}

function isExpired(session: GuestSession, now = Date.now()): boolean {
  const at = new Date(session.expires_at).getTime();
  return Number.isNaN(at) ? false : at <= now;
}

export function GuestSessionProvider({
  children,
  client: injectedClient,
}: {
  children: ReactNode;
  /** Tests inject a client; the app builds one from configuration. */
  client?: ApiClient;
}) {
  const stored = useRef<StoredSession | null>(readJson<StoredSession>(StorageKeys.session));

  const [session, setSession] = useState<GuestSession | null>(() => {
    const existing = stored.current?.session ?? null;
    return existing && !isExpired(existing) ? existing : null;
  });
  const [status, setStatus] = useState<SessionStatus>(() =>
    stored.current?.session && !isExpired(stored.current.session) ? 'active' : 'idle',
  );
  const [error, setError] = useState<ApiError | null>(null);

  // The client reads the token through a ref so that it never closes over a
  // stale value: a request started before a refresh must still finish under the
  // credential that is current when the header is written, not when the
  // component rendered.
  const tokenRef = useRef<string | null>(session?.session_token ?? null);
  const tenantRef = useRef<string | null>(session?.tenant_id ?? null);
  tokenRef.current = session?.session_token ?? null;
  tenantRef.current = session?.tenant_id ?? null;

  const client = useMemo(
    () =>
      injectedClient ??
      new ApiClient({
        baseUrl: readConfig().apiBaseUrl,
        getToken: () => tokenRef.current,
        getTenantId: () => tenantRef.current,
      }),
    [injectedClient],
  );

  const persist = useCallback((next: StoredSession | null) => {
    stored.current = next;
    if (next) writeJson(StorageKeys.session, next);
    else remove(StorageKeys.session);
  }, []);

  const clear = useCallback(() => {
    persist(null);
    setSession(null);
    setStatus('idle');
    setError(null);
  }, [persist]);

  const open = useCallback(
    async (input: OpenSessionInput) => {
      // Re-scanning the same table QR while a live session exists is a resume,
      // not a new session. This is the common case: a guest reopens the page
      // from their camera roll rather than from the tab they left open.
      if (
        stored.current &&
        stored.current.tableQrToken === input.tableQrToken &&
        !isExpired(stored.current.session)
      ) {
        setSession(stored.current.session);
        setStatus('active');
        return;
      }

      setStatus('opening');
      setError(null);

      const request: OpenGuestSessionRequest = {
        table_qr_token: input.tableQrToken,
        tenant_id: input.tenantId,
        table_id: input.tableId,
        service_mode: input.serviceMode,
        preferred_language: input.preferredLanguage
          ? BCP47[input.preferredLanguage]
          : undefined,
      };

      try {
        const opened = await client.openGuestSession(request, newIdempotencyKey());
        persist({
          session: opened,
          tableQrToken: input.tableQrToken,
          serviceMode: input.serviceMode,
        });
        setSession(opened);
        setStatus('active');
      } catch (caught) {
        const failure =
          caught instanceof ApiError
            ? caught
            : new ApiError({kind: 'parse', message: String(caught)});
        setError(failure);
        persist(null);
        setSession(null);
        // A token the server does not recognise is Q19's business — the table's
        // QR was regenerated, or this is not a QR for this venue at all. A
        // network failure is not: the QR may well be fine.
        setStatus(
          failure.kind === 'network'
            ? 'error'
            : failure.status === 404 || failure.code === 'validation_failed'
              ? 'invalid'
              : failure.isSessionProblem
                ? 'expired'
                : 'error',
        );
      }
    },
    [client, persist],
  );

  const refresh = useCallback(async () => {
    const credential = tokenRef.current;
    if (!credential) return;
    try {
      const current = await client.getGuestSession();
      // A scan opened a different table while this read was in flight. This
      // confirm is for the session the guest just left; writing it back would
      // revert the swap — and this refresh runs once on every start, so a guest
      // who reopens the app by scanning a *new* table is exactly the case that
      // races it. `open()` writes the new session to `stored` synchronously, so
      // a credential that no longer matches means a newer session won; drop this
      // stale confirm rather than clobber it.
      if (stored.current && stored.current.session.session_token !== credential) return;
      const previous = stored.current;
      // **The credential is kept, never taken from the reply.**
      //
      // `GET /v1/guest/session` answers with the same `GuestSession` schema the
      // create call does, but a read cannot re-issue a bearer token — the server
      // sends `session_token: ""`. Writing that reply over the stored session
      // **erased the credential**, and this refresh runs once on every start, so
      // it happened on the first reload, app-switch, or return from the camera
      // roll.
      //
      // What the guest then saw: the menu still loaded (that read is anonymous)
      // and "Send to the kitchen" answered `401 unauthenticated`, which
      // `isSessionProblem` turns into **"This QR has expired. Scan it again."**
      // The card was fine. A merchant who follows that instruction reprints the
      // table's card and invalidates the one already on the table.
      //
      // Found by driving the real surface: the order simply never arrived, and
      // the server logs 4xx at DEBUG, so nothing recorded the refusal at either
      // end.
      const confirmed: GuestSession = {
        ...current,
        session_token: current.session_token || credential,
      };
      persist({
        session: confirmed,
        tableQrToken: previous?.tableQrToken ?? '',
        serviceMode: previous?.serviceMode ?? 'dine_in',
      });
      setSession(confirmed);
      setStatus('active');
      setError(null);
    } catch (caught) {
      if (caught instanceof ApiError && caught.isSessionProblem) {
        // The sitting is over — the bill was paid and cleared, or voided, so the
        // server now refuses this session. Wipe everything it left behind, not
        // just the session token: a stale cart or a spent order id resuming into
        // the next sitting is exactly what this clear-out prevents (owner,
        // 2026-08-18). The screen then falls to Q19 ("scan again").
        clearGuestData();
        setSession(null);
        setError(caught);
        setStatus('expired');
        return;
      }
      // Offline is normal (foundations §6). Keep the session and keep selling —
      // a dropped refresh is not evidence the credential went bad.
      if (caught instanceof ApiError && caught.kind === 'network') return;
      throw caught;
    }
  }, [client, persist]);

  // Confirm a restored session with the server once, on start. The client's own
  // `expires_at` is a hint; the server's answer is the fact.
  const confirmed = useRef(false);
  useEffect(() => {
    if (confirmed.current || !tokenRef.current) return;
    confirmed.current = true;
    void refresh();
  }, [refresh]);

  const value = useMemo<GuestSessionValue>(
    () => ({status, session, error, client, open, refresh, clear}),
    [status, session, error, client, open, refresh, clear],
  );

  return (
    <GuestSessionContext.Provider value={value}>{children}</GuestSessionContext.Provider>
  );
}

export function useGuestSession(): GuestSessionValue {
  const value = useContext(GuestSessionContext);
  if (!value) {
    throw new Error('useGuestSession must be used inside <GuestSessionProvider>');
  }
  return value;
}

/** Shorthand for the screens that only need to talk to the API. */
export function useApi(): ApiClient {
  return useGuestSession().client;
}
